import { Button, EmptyState } from '@orbind/ui'
import { useCallback, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  adminSectionToggleCode,
  isEntitlementAdminSection,
  isEntitlementModule,
  moduleAccessKey,
  moduleToggleCode,
} from '@orbind/core'
import { activeModule } from '../shell/modules.ts'
import { usePermissionBundle } from './access-control.ts'
import { useFeatureToggleBundle } from './feature-toggle.ts'

/**
 * Gate de modulos vendaveis (SPEC-20260814-1333).
 *
 * Enquanto o bundle carrega, o modulo e tratado como LIGADO: esconder o rail
 * inteiro a cada boot seria um flash feio, e o que protege de verdade e o
 * `requireToggle` na API — este gate e navegacao, nao seguranca. Com o bundle
 * carregado, modulo fora do plano some do rail e deep-link cai na tela
 * "modulo nao contratado".
 */
/** Módulos ainda em teste, atrás de um toggle RELEASE (SPEC-20261009-2000). */
const MODULOS_EM_TESTE: Record<string, string> = { office: 'ESCRITORIO' }

export function useModuleGates() {
  const bundle = useFeatureToggleBundle()
  const toggles = bundle.data?.toggles
  const permBundle = usePermissionBundle()
  const permissions = permBundle.data?.permissions

  /**
   * O modulo aparece/abre para o usuario atual?
   * Entitlement do workspace (plano) AND permissao de modulo do perfil
   * (SPEC-20260814-1444). Fail-open enquanto os bundles carregam.
   */
  const moduleEnabled = useCallback(
    (moduleId: string): boolean => {
      // Módulo em teste: fail-CLOSED — só aparece com o toggle de release ligado.
      const release = MODULOS_EM_TESTE[moduleId]
      if (release && !(toggles?.[release] ?? false)) return false
      if (isEntitlementModule(moduleId)) {
        if (toggles !== undefined && !(toggles[moduleToggleCode(moduleId)] ?? false)) return false
      }
      if (permissions !== undefined && !(permissions[moduleAccessKey(moduleId)] ?? true)) return false
      return true
    },
    [toggles, permissions],
  )

  /** A tela da Administracao aparece/abre? (SPEC-20260814-1427) */
  const adminSectionEnabled = useCallback(
    (sectionId: string): boolean => {
      if (!isEntitlementAdminSection(sectionId)) return true
      if (toggles === undefined) return true
      return toggles[adminSectionToggleCode(sectionId)] ?? false
    },
    [toggles],
  )
  return { moduleEnabled, adminSectionEnabled }
}

/** Tela de bloqueio de uma secao do admin fora do plano do workspace. */
export function AdminSectionBlocked({ label }: { label: string }) {
  const navigate = useNavigate()
  return (
    <div className="adm-placeholder">
      <EmptyState
        icon="fas fa-lock"
        title={`${label} não está no seu plano`}
        description="Este cadastro não faz parte do plano contratado pelo seu workspace. Fale com o responsável pela conta para ampliar o plano."
        action={
          <Button variant="primary" icon="fas fa-list" onClick={() => navigate('/admin')}>
            Ver os cadastros disponíveis
          </Button>
        }
      />
    </div>
  )
}

/**
 * Envolve o `<Outlet/>` do shell: se a rota atual pertence a um modulo que o
 * workspace nao contratou, mostra a tela de bloqueio em vez do conteudo.
 */
export function ModuleGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { moduleEnabled } = useModuleGates()
  const toggles = useFeatureToggleBundle().data?.toggles

  const module = activeModule(location.pathname)
  if (module && !moduleEnabled(module.id)) {
    // distingue "o workspace nao comprou" (plano) de "seu perfil nao permite"
    const foraDoPlano =
      isEntitlementModule(module.id) && toggles !== undefined && !(toggles[moduleToggleCode(module.id)] ?? false)
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
        <EmptyState
          icon="fas fa-lock"
          title={
            foraDoPlano ? `${module.fallbackLabel} não está no seu plano` : `${module.fallbackLabel} fora do seu perfil`
          }
          description={
            foraDoPlano
              ? 'Este módulo não faz parte do plano contratado pelo seu workspace. Fale com o responsável pela conta para ampliar o plano — assim que o módulo for liberado, ele aparece aqui sem precisar de nova versão.'
              : 'Seu perfil de acesso não inclui este módulo. Fale com um administrador do workspace para ajustar seus perfis ou grupos.'
          }
          action={
            <Button variant="primary" icon="fas fa-home" onClick={() => navigate('/home')}>
              Voltar ao início
            </Button>
          }
        />
      </div>
    )
  }
  return <>{children}</>
}
